import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { restaurantAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function RestaurantDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const [vegFilter, setVegFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchRestaurant();
    // Load carts for all restaurants
    const savedCarts = JSON.parse(localStorage.getItem('carts') || '{}');
    // Get cart for this specific restaurant
    setCart(savedCarts[id] || []);
  }, [id, location.search]);

  const fetchRestaurant = async () => {
    try {
      const response = await restaurantAPI.getById(id);
      setRestaurant(response.data.data.restaurant);
      setMenuItems(response.data.data.menu);
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item) => {
    // Load all restaurant carts
    const allCarts = JSON.parse(localStorage.getItem('carts') || '{}');

    // Get or create cart for this restaurant
    const restaurantCart = allCarts[id] || [];

    const existingItem = restaurantCart.find(ci => ci._id === item._id);
    let newRestaurantCart;

    if (existingItem) {
      // Increase quantity of existing item
      newRestaurantCart = restaurantCart.map(ci =>
        ci._id === item._id ? { ...ci, quantity: ci.quantity + 1 } : ci
      );
    } else {
      // Add new item to this restaurant's cart
      newRestaurantCart = [...restaurantCart, {
        ...item,
        quantity: 1,
        restaurantId: id,
        restaurantName: restaurant.name
      }];
    }

    // Update the cart for this restaurant
    allCarts[id] = newRestaurantCart;

    // Save all carts back to localStorage
    localStorage.setItem('carts', JSON.stringify(allCarts));

    // Update local state
    setCart(newRestaurantCart);
  };

  const viewCart = () => {
    navigate('/user/cart');
  };

  if (loading) return <div className="loading">🔄 Loading restaurant...</div>;
  if (!restaurant) return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>❌</div>
          <h2 style={{ color: 'var(--gray-700)' }}>Restaurant not found</h2>
        </div>
      </div>
    </div>
  );

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Check if item matches search query
  const itemMatchesSearch = (item, query) => {
    if (!query.trim()) return false;
    const q = query.toLowerCase();
    return item.name.toLowerCase().includes(q) ||
           (item.description && item.description.toLowerCase().includes(q)) ||
           (item.category && item.category.toLowerCase().includes(q));
  };

  // Helper function to format time from 24-hour to 12-hour format
  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Filter and sort menu items
  const getFilteredAndSortedItems = () => {
    // Show all items that are marked as available, regardless of timing
    let items = menuItems.filter(item => item.isAvailable);

    // Get search query from URL (stored in state)
    const params = new URLSearchParams(location.search);
    const urlSearchQuery = params.get('search') || '';

    // Apply manual search filter (from search bar on this page)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.name.toLowerCase().includes(query) ||
        (item.description && item.description.toLowerCase().includes(query)) ||
        (item.category && item.category.toLowerCase().includes(query))
      );
    }

    // Apply veg filter
    if (vegFilter === 'veg') {
      items = items.filter(item => item.isVegetarian === true);
    } else if (vegFilter === 'non-veg') {
      items = items.filter(item => item.isVegetarian === false);
    }

    // Separate matching items (from URL search) and others
    if (urlSearchQuery.trim()) {
      const matchingItems = items.filter(item => itemMatchesSearch(item, urlSearchQuery));
      const otherItems = items.filter(item => !itemMatchesSearch(item, urlSearchQuery));

      // Sort each group
      const sortItems = (itemsToSort) => {
        if (sortBy === 'priceLowToHigh') {
          return itemsToSort.sort((a, b) => a.price - b.price);
        } else if (sortBy === 'priceHighToLow') {
          return itemsToSort.sort((a, b) => b.price - a.price);
        } else if (sortBy === 'name') {
          return itemsToSort.sort((a, b) => a.name.localeCompare(b.name));
        }
        return itemsToSort;
      };

      // Return matching items first, then others
      return [...sortItems([...matchingItems]), ...sortItems([...otherItems])];
    }

    // Apply sorting (when no URL search query)
    if (sortBy === 'priceLowToHigh') {
      items.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'priceHighToLow') {
      items.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name') {
      items.sort((a, b) => a.name.localeCompare(b.name));
    }

    return items;
  };

  const displayedItems = getFilteredAndSortedItems();

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh', paddingBottom: '100px' }}>
      <Navbar />
      <div className="container">
        {/* Restaurant Header */}
        <div className="card fade-in" style={{
          marginBottom: 'var(--space-2xl)',
          background: 'var(--gradient-primary)',
          color: 'white',
          border: 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-xl)', flexWrap: 'wrap' }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: 'var(--radius-lg)',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              backdropFilter: 'blur(10px)'
            }}>
              🏪
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: 'var(--font-size-4xl)',
                fontWeight: '800',
                marginBottom: 'var(--space-sm)',
                color: 'white'
              }}>
                {restaurant.name}
              </h1>
              <p style={{
                fontSize: 'var(--font-size-lg)',
                marginBottom: 'var(--space-md)',
                opacity: 0.95
              }}>
                {restaurant.description}
              </p>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-md)',
                marginTop: 'var(--space-lg)'
              }}>
                <div style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <span>🍽️</span>
                  <span style={{ fontWeight: '600' }}>{restaurant.cuisineType?.join(', ')}</span>
                </div>
                <div style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <span>⭐</span>
                  <span style={{ fontWeight: '700' }}>{restaurant.rating || 'New'}</span>
                </div>
                <div style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <span>📍</span>
                  <span style={{ fontWeight: '600' }}>{restaurant.address.street}, {restaurant.address.city}</span>
                </div>
                <div style={{
                  padding: 'var(--space-sm) var(--space-lg)',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 'var(--radius-md)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)'
                }}>
                  <span>📱</span>
                  <span style={{ fontWeight: '600' }}>{restaurant.phone}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Menu Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h2 style={{
            fontSize: 'var(--font-size-3xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            🍽️ Our Menu
          </h2>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Choose from our delicious selection
          </p>
        </div>

        {/* Filter and Sort Controls */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)', padding: 'var(--space-lg)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search Bar */}
            <div style={{ flex: '1 1 300px' }}>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-xs)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '600',
                color: 'var(--gray-700)'
              }}>
                🔍 Search Menu
              </label>
              <input
                type="text"
                placeholder="Search by name, description, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--space-sm) var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid var(--gray-200)',
                  fontSize: 'var(--font-size-base)',
                  backgroundColor: 'white',
                  transition: 'all var(--transition-base)',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary-color)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--gray-200)'}
              />
            </div>

            {/* Filter by Veg/Non-Veg */}
            <div style={{ flex: '1 1 200px' }}>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-xs)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '600',
                color: 'var(--gray-700)'
              }}>
                🥗 Filter
              </label>
              <select
                value={vegFilter}
                onChange={(e) => setVegFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--space-sm) var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid var(--gray-200)',
                  fontSize: 'var(--font-size-base)',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)'
                }}
              >
                <option value="all">All Items</option>
                <option value="veg">🌱 Vegetarian Only</option>
                <option value="non-veg">🍗 Non-Vegetarian Only</option>
              </select>
            </div>

            {/* Sort By */}
            <div style={{ flex: '1 1 200px' }}>
              <label style={{
                display: 'block',
                marginBottom: 'var(--space-xs)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '600',
                color: 'var(--gray-700)'
              }}>
                🔄 Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--space-sm) var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid var(--gray-200)',
                  fontSize: 'var(--font-size-base)',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)'
                }}
              >
                <option value="default">Default</option>
                <option value="priceLowToHigh">💰 Price: Low to High</option>
                <option value="priceHighToLow">💎 Price: High to Low</option>
                <option value="name">🔤 Name (A-Z)</option>
              </select>
            </div>

            {/* Results Count */}
            <div style={{
              flex: '1 1 auto',
              textAlign: 'right',
              color: 'var(--gray-600)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: '600'
            }}>
              {displayedItems.length} {displayedItems.length === 1 ? 'item' : 'items'} found
            </div>
          </div>
        </div>

        {/* Menu Grid */}
        <div className="grid">
          {displayedItems.map(item => {
            const params = new URLSearchParams(location.search);
            const urlSearchQuery = params.get('search') || '';
            const isSearchedItem = itemMatchesSearch(item, urlSearchQuery);

            return (
            <div key={item._id} className="card fade-in" style={{
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              border: isSearchedItem ? '3px solid var(--primary-color)' : undefined
            }}>
              {/* Searched Item Badge */}
              {isSearchedItem && (
                <div style={{
                  position: 'absolute',
                  top: 'var(--space-md)',
                  left: 'var(--space-md)',
                  padding: 'var(--space-xs) var(--space-md)',
                  background: 'var(--gradient-primary)',
                  color: 'white',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  boxShadow: 'var(--shadow-md)',
                  zIndex: 2
                }}>
                  <span>🔍</span>
                  <span>SEARCHED ITEM</span>
                </div>
              )}

              {/* Vegetarian Badge */}
              {item.isVegetarian && (
                <div style={{
                  position: 'absolute',
                  top: 'var(--space-md)',
                  right: 'var(--space-md)',
                  padding: 'var(--space-xs) var(--space-md)',
                  background: 'var(--gradient-success)',
                  color: 'white',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  boxShadow: 'var(--shadow-sm)',
                  zIndex: 1
                }}>
                  <span>🌱</span>
                  <span>VEG</span>
                </div>
              )}

              {/* Out of Stock Badge */}
              {item.outOfStock?.isOutOfStock && (
                <div style={{
                  position: 'absolute',
                  top: item.isVegetarian ? 'calc(var(--space-md) + 40px)' : 'var(--space-md)',
                  right: 'var(--space-md)',
                  padding: 'var(--space-xs) var(--space-md)',
                  background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                  color: 'white',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  boxShadow: 'var(--shadow-md)',
                  zIndex: 1
                }}>
                  <span>📦</span>
                  <span>OUT OF STOCK</span>
                </div>
              )}

              {/* Availability Timing Badge */}
              {!item.outOfStock?.isOutOfStock && item.availabilityTiming?.enabled && !item.isCurrentlyAvailable && (
                <div style={{
                  position: 'absolute',
                  top: item.isVegetarian ? 'calc(var(--space-md) + 40px)' : 'var(--space-md)',
                  right: 'var(--space-md)',
                  padding: 'var(--space-xs) var(--space-md)',
                  background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)',
                  color: 'white',
                  borderRadius: 'var(--radius-full)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  boxShadow: 'var(--shadow-md)',
                  zIndex: 1
                }}>
                  <span>🕐</span>
                  <span>TIME RESTRICTED</span>
                </div>
              )}

              {/* Menu Item Image */}
              {item.image && (
                <div style={{
                  width: '100%',
                  height: '180px',
                  marginBottom: 'var(--space-md)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden',
                  background: 'var(--gray-100)'
                }}>
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Item Info */}
              <div style={{ flex: 1, paddingRight: '80px' }}>
                <h3 style={{
                  fontSize: 'var(--font-size-2xl)',
                  fontWeight: '700',
                  marginBottom: 'var(--space-sm)',
                  color: 'var(--gray-900)'
                }}>
                  {item.name}
                </h3>
                <p style={{
                  color: 'var(--gray-600)',
                  fontSize: 'var(--font-size-sm)',
                  marginBottom: 'var(--space-md)',
                  lineHeight: '1.5'
                }}>
                  {item.description}
                </p>
                <div style={{
                  display: 'inline-block',
                  padding: 'var(--space-xs) var(--space-md)',
                  background: 'rgba(78, 205, 196, 0.1)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid rgba(78, 205, 196, 0.2)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: '600', color: 'var(--secondary)' }}>
                    {item.category}
                  </span>
                </div>
              </div>

              {/* Out of Stock Message */}
              {item.outOfStock?.isOutOfStock && (
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'linear-gradient(135deg, rgba(231, 76, 60, 0.1) 0%, rgba(192, 57, 43, 0.1) 100%)',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid rgba(231, 76, 60, 0.3)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)'
                  }}>
                    <span style={{ fontSize: 'var(--font-size-lg)' }}>📦</span>
                    <span style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '700',
                      color: '#e74c3c'
                    }}>
                      Out of Stock - Will be available tomorrow
                    </span>
                  </div>
                </div>
              )}

              {/* Availability Timing Message */}
              {!item.outOfStock?.isOutOfStock && item.availabilityTiming?.enabled && !item.isCurrentlyAvailable && (
                <div style={{
                  padding: 'var(--space-md)',
                  background: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)',
                  borderRadius: 'var(--radius-md)',
                  border: '2px solid rgba(243, 156, 18, 0.3)',
                  marginBottom: 'var(--space-md)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    marginBottom: 'var(--space-xs)'
                  }}>
                    <span style={{ fontSize: 'var(--font-size-lg)' }}>🕐</span>
                    <span style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '700',
                      color: '#e67e22'
                    }}>
                      Available during specific hours
                    </span>
                  </div>
                  <p style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--gray-700)',
                    margin: 0,
                    paddingLeft: 'calc(var(--font-size-lg) + var(--space-sm))'
                  }}>
                    {formatTime(item.availabilityTiming.startTime)} - {formatTime(item.availabilityTiming.endTime)}
                  </p>
                </div>
              )}

              {/* Price and Add Button */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 'var(--space-md)',
                borderTop: '2px solid var(--gray-100)',
                marginTop: 'auto'
              }}>
                <p style={{
                  fontSize: 'var(--font-size-2xl)',
                  fontWeight: '800',
                  color: 'var(--primary)',
                  margin: 0
                }}>
                  ₹{item.price}
                </p>
                <button
                  onClick={() => item.isCurrentlyAvailable !== false && addToCart(item)}
                  className="btn btn-primary"
                  disabled={item.isCurrentlyAvailable === false}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-sm)',
                    opacity: item.isCurrentlyAvailable === false ? 0.5 : 1,
                    cursor: item.isCurrentlyAvailable === false ? 'not-allowed' : 'pointer'
                  }}
                >
                  <span>{item.isCurrentlyAvailable === false ? '🔒' : '➕'}</span>
                  <span>{item.isCurrentlyAvailable === false ? 'Not Available' : 'Add to Cart'}</span>
                </button>
              </div>

              {/* Hover Accent */}
              <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background: 'radial-gradient(circle at top right, rgba(255, 107, 107, 0.1) 0%, transparent 70%)',
                pointerEvents: 'none'
              }} />
            </div>
            );
          })}
        </div>

        {displayedItems.length === 0 && (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>🍽️</div>
            <h3 style={{ color: 'var(--gray-700)', marginBottom: 'var(--space-md)' }}>
              {menuItems.filter(item => item.isAvailable).length === 0
                ? 'No menu items available'
                : 'No items match your filters'}
            </h3>
            <p style={{ color: 'var(--gray-600)' }}>
              {menuItems.filter(item => item.isAvailable).length === 0
                ? 'Please check back later'
                : 'Try adjusting your filter or sort settings'}
            </p>
          </div>
        )}

        {/* Floating Cart Button */}
        {cart.length > 0 && (
          <div style={{
            position: 'fixed',
            bottom: 'var(--space-xl)',
            right: 'var(--space-xl)',
            background: 'var(--gradient-primary)',
            color: 'white',
            padding: 'var(--space-lg) var(--space-xl)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-xl)',
            cursor: 'pointer',
            minWidth: '250px',
            transition: 'all var(--transition-base)',
            zIndex: 1000
          }}
          onClick={viewCart}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--space-sm)'
            }}>
              <span style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                <span>🛒</span>
                <span>View Cart</span>
              </span>
              <span style={{
                padding: 'var(--space-xs) var(--space-md)',
                background: 'rgba(255, 255, 255, 0.25)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: '700'
              }}>
                {cartCount} {cartCount === 1 ? 'item' : 'items'}
              </span>
            </div>
            <div style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: '800',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>Total: ₹{cartTotal}</span>
              <span style={{ fontSize: 'var(--font-size-lg)' }}>→</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RestaurantDetails;
